import React from 'react';
import {
  C, CARD_W, nx, ny,
  GOAL_CARD_W, GOAL_CARD_H_CONTENT, GOAL_CARD_GAP,
  GOALS_HEADER_H, GOALS_NODE_PADDING, GOALS_NODE_H,
  goalsNodeHalfW,
  resolveFeatureIconAndLabel,
  imgUrl, normalizeImgField, normalizeAudioField,
} from '../flowCanvas.constants';
import AudioWaveformCell from './AudioWaveformCell';

// ─── GoalsNode ─────────────────────────────────────────────────────────────────
// Horizontal strip of goal cards sorted by %.  Each card shows:
//   • % badge + goal name + trash button
//   • Locked / Unlocked images stacked (each clickable → opens image editor)
//   • Rewards sub-panel: title | reward image | disruptor+⏸+⏹ | audio
// ──────────────────────────────────────────────────────────────────────────────

const BADGE_W      = 44;
const BADGE_H      = 22;
const INNER_PAD    = 10;
const INNER_W      = GOAL_CARD_W - INNER_PAD * 2;
const TRASH_R      = 9;
const CARD_H_INNER = GOAL_CARD_H_CONTENT - 12;

// Row dimensions inside rewards panel
const ROW_H       = 20;
const ROW_ICON_R  = 8;
const ROW_GAP     = 4;

// Y offsets from cardY (card rect top-left)
const Y_NAME           = 16;
const Y_BADGE          = 38;
const Y_DIVIDER_1      = 66;
const Y_LOCKED_LABEL   = 81;
const Y_LOCKED_IMG     = 89;
const IMG_H            = 48;
const Y_UNLOCKED_LABEL = Y_LOCKED_IMG + IMG_H + 9;
const Y_UNLOCKED_IMG   = Y_UNLOCKED_LABEL + 11;
const Y_REWARDS_RECT   = Y_UNLOCKED_IMG + IMG_H + 14;
const REWARDS_HDR_H    = 18;

// ── Mini waveform bars ────────────────────────────────────────────────────────
function MiniWaveform({ x, y, w = 28, h = 12, color }) {
  const bars = [0.5, 1, 0.7, 0.9, 0.4];
  const bW   = Math.max(1, Math.floor(w / bars.length) - 1);
  return (
    <g style={{ pointerEvents: 'none' }}>
      {bars.map((frac, idx) => {
        const bH = Math.max(1, Math.round(h * frac));
        return <rect key={idx} x={x + idx * (bW + 1)} y={y + h - bH}
          width={bW} height={bH} rx="0.8" fill={color} fillOpacity="0.75" />;
      })}
    </g>
  );
}

// ── Row icon: small circle with a short label ─────────────────────────────────
function RowIcon({ cx, cy, label, color }) {
  return (
    <g style={{ pointerEvents: 'none' }}>
      <circle cx={cx} cy={cy} r={ROW_ICON_R}
        fill={C.circle} stroke={color} strokeWidth="0.8" />
      <text x={cx} y={cy + 4} textAnchor="middle"
        fontSize="8" fontWeight="700" fill={color} style={{ userSelect: 'none' }}>
        {label}
      </text>
    </g>
  );
}

// ── "+ Add Xxx" dashed pill CTA ───────────────────────────────────────────────
function AddCTA({ x, y, label, w, h = ROW_H }) {
  return (
    <g style={{ pointerEvents: 'none' }}>
      <rect x={x} y={y} width={w} height={h} rx={h / 2}
        fill="rgba(255,255,255,0.03)"
        stroke="rgba(255,255,255,0.14)"
        strokeWidth="0.8" strokeDasharray="2 2" />
      <text x={x + w / 2} y={y + h / 2 + 4} textAnchor="middle"
        fontSize="8" fill={C.textDim} style={{ userSelect: 'none' }}>
        + {label}
      </text>
    </g>
  );
}

// ── Small toggle-style button (pause / end-previous) ─────────────────────────
function ToggleBtn({ x, y, w, h, active, activeColor, label, onClick }) {
  const [icon, ...rest] = String(label || '').split(' ');
  const text = rest.join(' ');
  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      <rect x={x} y={y} width={w} height={h} rx="3"
        fill={C.circle}
        stroke={active ? activeColor : C.border}
        strokeWidth="1" />
      <circle
        cx={x + 10}
        cy={y + h / 2}
        r="5.5"
        fill={active ? C.tintSuccess : C.circle}
        stroke={active ? activeColor : C.border}
        strokeWidth="0.8" />
      <text x={x + 10} y={y + h / 2 + 3} textAnchor="middle"
        fontSize="7.5" fontWeight="700" fill={active ? activeColor : C.textDim}
        style={{ userSelect: 'none', pointerEvents: 'none' }}>
        {icon}
      </text>
      <text x={x + 20} y={y + h / 2 + 3} textAnchor="start"
        fontSize="7.5" fontWeight="600" fill={active ? activeColor : C.textMuted}
        style={{ userSelect: 'none', pointerEvents: 'none' }}>
        {text}
      </text>
    </g>
  );
}

function DeleteBtn({ cx, cy, r = 7, onClick }) {
  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      <circle cx={cx} cy={cy} r={r} fill="#dc2626" />
      <g transform={`translate(${cx}, ${cy})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
        <rect x="-1.5" y="-4" width="3" height="1.5" rx="0.7" fill="#fee2e2" />
        <rect x="-3.5" y="-2.5" width="7" height="1.2" rx="0.6" fill="#fee2e2" />
        <rect x="-2.7" y="-1" width="5.4" height="4.8" rx="0.6" fill="#fee2e2" />
        <rect x="-1.2" y="0" width="0.7" height="3" rx="0.35" fill="#dc2626" />
        <rect x="0.5" y="0" width="0.7" height="3" rx="0.35" fill="#dc2626" />
      </g>
    </g>
  );
}

export default function GoalsNode({
  step,
  overrideX,
  overrideY,
  centreY,
  selected,
  onClick,
  liveCustomisationData,
  campaignFeatures,
  assetFolder,
  campaignId,
  activeGoalIndex,
  onActivate,
  onGoalActivate,
  onGoalFieldChange,
  onRemoveGoal,
  onRewardDisruptorDrop,
}) {
  const cx = overrideX ?? nx(step);
  const cy = overrideY ?? ny(step, centreY);

  // ── Resolve live goals ─────────────────────────────────────────────────────────────────────
  const tabKey  = step.customisationConfig?.tabKey;
  const varKey  = step.customisationConfig?.varKey;
  const varName = step.customisationConfig?.variables?.[0]?.variableName;
  const liveVals = liveCustomisationData && tabKey && varKey && varName
    ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName]
    : null;
  const rawGoals = Array.isArray(liveVals)
    ? liveVals
    : (step.populatedData?.variables?.[0]?.allValues || []);

  const goals = rawGoals
    .map((goal, sourceIndex) => ({ goal, sourceIndex }))
    .filter(({ goal }) => Boolean(goal))
    .sort((a, b) => (a.goal?.completePerc ?? 0) - (b.goal?.completePerc ?? 0));

  // ── Sizing ───────────────────────────────────────────────────────────────────────────────────
  const goalsAreaW = goals.length > 0
    ? goals.length * (GOAL_CARD_W + GOAL_CARD_GAP) - GOAL_CARD_GAP
    : GOAL_CARD_W;
  const totalW = goalsAreaW + GOALS_NODE_PADDING * 2;
  const halfW  = goalsNodeHalfW(Math.max(goals.length, 1));

  const leftX        = cx - halfW;
  const topY         = cy - GOALS_NODE_H / 2;
  const cardAreaTopY = topY + GOALS_HEADER_H;

  // ── Helpers ──────────────────────────────────────────────────────────────────────────────────
  const resolveThumb = (field) => {
    const { name: fn, isDefault, srcUrl } = normalizeImgField(field);
    if (srcUrl) return srcUrl;
    return imgUrl(fn, isDefault, assetFolder, campaignId);
  };

  const handleGoalClick = (e, i, subField) => {
    e.stopPropagation();
    if (onGoalActivate) onGoalActivate(i, subField);
  };

  const handleFieldChange = (e, i, field, value, openField = null) => {
    e.stopPropagation();
    onGoalFieldChange?.(i, field, value);
    if (openField) {
      if (onClick) onClick(e);
      if (onGoalActivate) onGoalActivate(i, openField);
    }
  };

  const nodeBorder = selected ? C.sel : C.purple;

  return (
    <g className="fc-node fc-node--goals">

      {/* ── Node outer rect + header ────────────────────────────────────────────────────────────────────── */}
      <rect x={leftX} y={topY} width={Math.max(totalW, CARD_W)} height={GOALS_NODE_H}
        rx="10" fill={selected ? C.cardSel : C.card}
        stroke={nodeBorder} strokeWidth={selected ? 2 : 1.5}
        style={{ pointerEvents: 'none' }} />
      <text x={leftX + 14} y={topY + 22}
        fontSize="11.5" fontWeight="700" fill={C.text}
        style={{ userSelect: 'none', pointerEvents: 'none' }}>
        Progress Goals
      </text>
      <text x={leftX + 14} y={topY + 38}
        fontSize="9" fill={C.textMuted}
        style={{ userSelect: 'none', pointerEvents: 'none' }}>
        Sorted by % — lowest to highest
      </text>
      <line x1={leftX + 1} y1={topY + GOALS_HEADER_H - 2}
        x2={leftX + Math.max(totalW, CARD_W) - 1} y2={topY + GOALS_HEADER_H - 2}
        stroke={C.border} strokeWidth="1" style={{ pointerEvents: 'none' }} />

      {goals.length === 0 && (
        <text
          x={leftX + GOAL_CARD_W / 2 + GOALS_NODE_PADDING}
          y={topY + GOALS_HEADER_H + CARD_H_INNER / 2 + 4}
          textAnchor="middle" fontSize="9" fill={C.textDim}
          style={{ userSelect: 'none', pointerEvents: 'none' }}>
          No goals yet
        </text>
      )}

      {/* ── Goal cards ────────────────────────────────────────────────────────────────────────────────── */}
      {goals.map(({ goal, sourceIndex }, i) => {
        const cardX = leftX + GOALS_NODE_PADDING + i * (GOAL_CARD_W + GOAL_CARD_GAP);
        const cardY = cardAreaTopY + 6;

        const perc     = goal.completePerc ?? 0;
        const hue      = Math.round(120 * (1 - perc / 100));
        const badgeClr = `hsl(${hue},72%,var(--fc-chance-text-l))`;

        const isActive    = activeGoalIndex === sourceIndex;
        const cardFill    = isActive ? 'rgba(192,96,255,0.09)' : C.card;
        const cardBorderC = isActive ? C.sel : C.border;
        const cardBW      = isActive ? 1.8 : 1;

        const name         = goal.goalName || '';
        const disruptorId  = goal.triggerDisruptorFeatureID || null;
        const { label: dlabel, icon: dicon } = resolveFeatureIconAndLabel(disruptorId, campaignFeatures);
        const truncLabel   = dlabel && dlabel.length > 14 ? dlabel.slice(0, 14) + '…' : (dlabel || '');
        const pauseActive  = !!goal.pauseProgress;
        const endActive    = !!goal.endPreviousDisruptors;
        const textEnabled  = !!goal.showGoalTitle;
        const imgEnabled   = !!goal.showGoalImage;
        const audioEnabled = !!goal.playSFX;
        const { name: sfxName } = normalizeAudioField(goal.sfxFilename);

        const lockedSrc    = resolveThumb(goal.lockedFilename);
        const unlockedSrc  = resolveThumb(goal.unlockedFilename);
        const rewardImgSrc = imgEnabled ? resolveThumb(goal.goalImageFilename) : null;

        const trashCX = cardX + GOAL_CARD_W - TRASH_R - 3;
        const trashCY = cardY + Y_NAME + 1;

        // Rewards container geometry
        const rX  = cardX + 4;
        const rY  = cardY + Y_REWARDS_RECT;
        const rW  = GOAL_CARD_W - 8;
        const rH  = CARD_H_INNER - Y_REWARDS_RECT - 4;
        const slotX = cardX + INNER_PAD;
        const slotW = INNER_W;

        // Reward input rows
        const row0Y = rY + 28;
        const row1Y = rY + 62;
        const inputH = 24;
        const dividerAfterRewardsTogglesY = rY + 238;
        const dividerAfterImageY = rY + 98;

        // Component trigger section
        const disruptorLabelY   = rY + 116;
        const disruptorSlotY    = rY + 126;
        const DISRUPTOR_SLOT_H  = 40;
        const disruptorSlotMidY = disruptorSlotY + DISRUPTOR_SLOT_H / 2;

        // Trigger options (stacked)
        const toggleLabelY = rY + 182;
        const toggleY  = rY + 192;
        const TOGGLE_H = 24;
        const tBtnW    = slotW;

        // Audio section
        const audioLabelY   = rY + 263;
        const audioSlotY    = rY + 268;
        const AUDIO_SLOT_H  = audioEnabled && sfxName ? 52 : 46;
        const audioSlotMidY = audioSlotY + AUDIO_SLOT_H / 2;

        return (
          <g key={`goal-${i}`}>

            {/* ── Card background ─────────────────────────────────────────────────────────────────── */}
            <rect x={cardX} y={cardY} width={GOAL_CARD_W} height={CARD_H_INNER}
              rx="8" fill={cardFill} stroke={cardBorderC} strokeWidth={cardBW}
              style={{ pointerEvents: 'none' }} />

            {/* Goal setup click zone: only header/title area opens Goal Setup */}
            <rect
              x={cardX}
              y={cardY}
              width={GOAL_CARD_W}
              height={Y_DIVIDER_1 - 4}
              rx="8"
              fill="transparent"
              onClick={(e) => handleGoalClick(e, sourceIndex, undefined)}
              style={{ cursor: 'pointer' }}
            />

            {/* % badge */}
            <rect x={cardX + INNER_PAD} y={cardY + Y_BADGE}
              width={BADGE_W} height={BADGE_H} rx="5"
              fill={C.circle} stroke={badgeClr} strokeWidth="1.2"
              style={{ pointerEvents: 'none' }} />
            <text x={cardX + INNER_PAD + BADGE_W / 2} y={cardY + Y_BADGE + BADGE_H / 2 + 4}
              textAnchor="middle" fontSize="11" fontWeight="700" fill={badgeClr}
              style={{ pointerEvents: 'none', userSelect: 'none' }}>
              {perc}%
            </text>

            {/* Goal name */}
            <foreignObject x={cardX + INNER_PAD} y={cardY + Y_NAME - 10} width={GOAL_CARD_W - INNER_PAD * 2 - 22} height="22">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}
              >
                <input
                  value={name}
                  placeholder={`Goal ${sourceIndex + 1}`}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onChange={(e) => onGoalFieldChange?.(sourceIndex, 'goalName', e.target.value)}
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: 'var(--fc-text)',
                      fontSize: '10.5px',
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    padding: 0,
                  }}
                />
              </div>
            </foreignObject>

            {/* Divider */}
            <line x1={cardX + 4} y1={cardY + Y_DIVIDER_1}
              x2={cardX + GOAL_CARD_W - 4} y2={cardY + Y_DIVIDER_1}
              stroke={C.border} strokeWidth="0.8" strokeOpacity="0.5"
              style={{ pointerEvents: 'none' }} />

            {/* ── Locked image ────────────────────────────────────────────────────────────────────── */}
            <g onClick={(e) => handleGoalClick(e, sourceIndex, 'lockedFilename')} style={{ cursor: 'pointer' }}>
              <text x={cardX + INNER_PAD} y={cardY + Y_LOCKED_LABEL}
                fontSize="9.5" fontWeight="600" fill={C.textMuted}
                style={{ userSelect: 'none', pointerEvents: 'none' }}>
                Locked
              </text>
              <rect x={slotX} y={cardY + Y_LOCKED_IMG}
                width={slotW} height={IMG_H} rx="4"
                fill={C.circle}
                stroke={C.border}
                strokeWidth="1" />
              {lockedSrc
                ? <image href={lockedSrc}
                    x={slotX} y={cardY + Y_LOCKED_IMG}
                    width={slotW} height={IMG_H}
                  preserveAspectRatio="xMidYMid meet"
                    style={{ pointerEvents: 'none' }} />
                : <text x={slotX + slotW / 2}
                    y={cardY + Y_LOCKED_IMG + IMG_H / 2 + 4}
                    textAnchor="middle" fontSize="8" fill={C.textDim}
                    style={{ pointerEvents: 'none', userSelect: 'none' }}>
                    No image set
                  </text>
              }
            </g>

            {/* ── Unlocked image ───────────────────────────────────────────────────────────────────── */}
            <g onClick={(e) => handleGoalClick(e, sourceIndex, 'unlockedFilename')} style={{ cursor: 'pointer' }}>
              <text x={cardX + INNER_PAD} y={cardY + Y_UNLOCKED_LABEL}
                fontSize="9.5" fontWeight="600" fill={C.textMuted}
                style={{ userSelect: 'none', pointerEvents: 'none' }}>
                Unlocked
              </text>
              <rect x={slotX} y={cardY + Y_UNLOCKED_IMG}
                width={slotW} height={IMG_H} rx="4"
                fill={C.circle}
                stroke={C.border}
                strokeWidth="1" />
              {unlockedSrc
                ? <image href={unlockedSrc}
                    x={slotX} y={cardY + Y_UNLOCKED_IMG}
                    width={slotW} height={IMG_H}
                  preserveAspectRatio="xMidYMid meet"
                    style={{ pointerEvents: 'none' }} />
                : <text x={slotX + slotW / 2}
                    y={cardY + Y_UNLOCKED_IMG + IMG_H / 2 + 4}
                    textAnchor="middle" fontSize="8" fill={C.textDim}
                    style={{ pointerEvents: 'none', userSelect: 'none' }}>
                    No image set
                  </text>
              }
            </g>

            {/* ── Rewards container ───────────────────────────────────────────────────────────────────── */}
            <rect x={rX} y={rY} width={rW} height={rH}
              rx="5" fill={C.circle}
              stroke={C.border} strokeWidth="0.8"
              style={{ pointerEvents: 'none' }} />
            <text x={slotX} y={rY + 13}
              fontSize="9.5" fontWeight="600" fill={C.textMuted}
              style={{ pointerEvents: 'none', userSelect: 'none' }}>
              REWARD TRIGGERS
            </text>
            <line x1={rX + 2} y1={rY + REWARDS_HDR_H - 1}
              x2={rX + rW - 2} y2={rY + REWARDS_HDR_H - 1}
              stroke={C.border} strokeWidth="0.8"
              style={{ pointerEvents: 'none' }} />

            {/* Row 0 — Display goal name toggle */}
            <ToggleBtn
              x={slotX} y={row0Y} w={slotW} h={inputH}
              active={textEnabled} activeColor={C.greenText} label="T Display Goal Name"
              onClick={(e) => handleFieldChange(e, sourceIndex, 'showGoalTitle', !textEnabled, 'showGoalTitle')} />

            {/* Row 1 — Reward image */}
            <g onClick={(e) => handleGoalClick(e, sourceIndex, 'goalImageFilename')} style={{ cursor: 'pointer' }}>
              <rect x={slotX} y={row1Y}
                width={slotW} height={24} rx="5"
                fill={imgEnabled && rewardImgSrc ? 'rgba(145,70,255,0.09)' : C.circle}
                stroke={imgEnabled && rewardImgSrc ? C.border : C.textDim}
                strokeWidth="1"
                strokeDasharray={imgEnabled && rewardImgSrc ? undefined : '4 3'} />
              {imgEnabled && rewardImgSrc ? (
                <g style={{ pointerEvents: 'none' }}>
                  <rect x={slotX + 4} y={row1Y + 3}
                    width={18} height={18} rx="3"
                    fill={C.circle} stroke={C.border} strokeWidth="0.8" />
                  <image href={rewardImgSrc}
                    x={slotX + 4} y={row1Y + 3}
                    width={18} height={18}
                    preserveAspectRatio="xMidYMid meet" />
                  <text x={slotX + 28} y={row1Y + 15}
                    fontSize="8.5" fill={C.textMuted}
                    style={{ userSelect: 'none' }}>
                    Reward Image
                  </text>
                </g>
              ) : (
                <text x={slotX + slotW / 2} y={row1Y + 16}
                  textAnchor="middle" fontSize="9.5" fill={C.textDim}
                  style={{ userSelect: 'none', pointerEvents: 'none' }}>
                  + Add Image
                </text>
              )}
              {!imgEnabled && (
                <rect x={slotX} y={row1Y} width={slotW} height={24} rx="5" fill="transparent"
                  onClick={(e) => handleFieldChange(e, sourceIndex, 'showGoalImage', true, 'goalImageFilename')} />
              )}
              {imgEnabled && (
                <DeleteBtn cx={slotX + slotW - 10} cy={row1Y + 12} r={7}
                  onClick={(e) => handleFieldChange(e, sourceIndex, 'showGoalImage', false, null)} />
              )}
            </g>

            {/* Divider before disruptor */}
            <line x1={slotX} y1={dividerAfterImageY}
              x2={slotX + slotW} y2={dividerAfterImageY}
              stroke={C.border} strokeWidth="0.8"
              style={{ pointerEvents: 'none' }} />

            {/* ── Disruptor section (PollOutcomeNode style) ─────────────────────────────────────────────── */}
            <text x={slotX} y={disruptorLabelY}
              fontSize="9.5" fontWeight="600" fill={C.textMuted}
              style={{ pointerEvents: 'none', userSelect: 'none' }}>
              COMPONENT TRIGGER
            </text>
            <g
              onClick={(e) => handleGoalClick(e, sourceIndex, 'triggerDisruptorFeatureID')}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onRewardDisruptorDrop) onRewardDisruptorDrop(sourceIndex, e);
              }}
              style={{ cursor: 'pointer' }}>
              <rect x={slotX} y={disruptorSlotY}
                width={slotW} height={DISRUPTOR_SLOT_H} rx="8"
                fill={disruptorId ? 'rgba(145,70,255,0.09)' : 'rgba(255,255,255,0.03)'}
                stroke={disruptorId ? C.purple : C.border}
                strokeWidth={disruptorId ? 1.2 : 1}
                strokeDasharray={disruptorId ? undefined : '4 3'} />
              {disruptorId ? (
                <g style={{ pointerEvents: 'none' }}>
                  <circle
                    cx={slotX + 18} cy={disruptorSlotMidY} r={10}
                    fill="rgba(145,70,255,0.15)" stroke="rgba(145,70,255,0.35)" strokeWidth="1" />
                  <text x={slotX + 18} y={disruptorSlotMidY + 5}
                    textAnchor="middle" fontSize="12"
                    style={{ userSelect: 'none' }}>{dicon}</text>
                  <text x={slotX + 34} y={disruptorSlotMidY + 4}
                    fontSize="9.5" fontWeight="700" fill={C.text}
                    style={{ userSelect: 'none' }}>{truncLabel}</text>
                </g>
              ) : (
                <text x={slotX + slotW / 2} y={disruptorSlotMidY + 4}
                  textAnchor="middle" fontSize="10" fill={C.textDim}
                  style={{ userSelect: 'none', pointerEvents: 'none' }}>
                  Drag a component here
                </text>
              )}
              {disruptorId && (
                <DeleteBtn cx={slotX + slotW - 10} cy={disruptorSlotY + 10} r={7}
                  onClick={(e) => handleFieldChange(e, sourceIndex, 'triggerDisruptorFeatureID', '', null)} />
              )}
            </g>

            {/* Toggle row label */}
            <text x={slotX} y={toggleLabelY}
              fontSize="7.5" fontWeight="700" letterSpacing="0.5" fill={C.textDim}
              style={{ pointerEvents: 'none', userSelect: 'none' }}>
              TRIGGER OPTIONS
            </text>

            {/* ── Pause / End-previous toggle buttons ───────────────────────────────────────────────────────────────── */}
            <ToggleBtn
              x={slotX} y={toggleY} w={tBtnW} h={TOGGLE_H}
              active={pauseActive} activeColor={C.neonBlue} label="⏸ Pause Progress"
              onClick={(e) => handleFieldChange(e, sourceIndex, 'pauseProgress', !pauseActive, 'pauseProgress')} />
            <ToggleBtn
              x={slotX} y={toggleY + TOGGLE_H + 4} w={tBtnW} h={TOGGLE_H}
              active={endActive} activeColor={C.red} label="⏹   End Previous Trigger"
              onClick={(e) => handleFieldChange(e, sourceIndex, 'endPreviousDisruptors', !endActive, 'endPreviousDisruptors')} />

            {/* Divider before audio */}
            <line x1={slotX} y1={dividerAfterRewardsTogglesY}
              x2={slotX + slotW} y2={dividerAfterRewardsTogglesY}
              stroke={C.border} strokeWidth="0.8"
              style={{ pointerEvents: 'none' }} />

            {/* ── Audio section (PollOutcomeNode style) ───────────────────────────────────────────────────────────── */}
            <text x={slotX} y={audioLabelY}
              fontSize="9.5" fontWeight="600" fill={C.textMuted}
              style={{ pointerEvents: 'none', userSelect: 'none' }}>
              AUDIO TRIGGER
            </text>
            <g onClick={(e) => handleGoalClick(e, sourceIndex, 'sfxFilename')} style={{ cursor: 'pointer' }}>
              <rect x={slotX} y={audioSlotY}
                width={slotW} height={AUDIO_SLOT_H} rx="5"
                fill={audioEnabled && sfxName ? 'rgba(145,70,255,0.09)' : 'none'}
                stroke={audioEnabled && sfxName ? 'rgba(145,70,255,0.28)' : C.textDim}
                strokeWidth="1"
                strokeDasharray={audioEnabled && sfxName ? undefined : '4 3'} />
              {audioEnabled && sfxName ? (
                <g>
                  <text x={slotX + 10} y={audioSlotY + 19}
                    fontSize="14" fill={C.purple}
                    style={{ userSelect: 'none', pointerEvents: 'none' }}>♪</text>
                  <text x={slotX + 28} y={audioSlotY + 19}
                    fontSize="10.5" fontWeight="600" fill={C.text}
                    style={{ userSelect: 'none', pointerEvents: 'none' }}>
                    {sfxName.length > 14 ? sfxName.slice(0, 14) + '…' : sfxName}
                  </text>
                  <foreignObject x={slotX + 8} y={audioSlotY + 24} width={slotW - 16} height="20" style={{ pointerEvents: 'auto' }}>
                    <AudioWaveformCell srcUrl={normalizeAudioField(goal.sfxFilename).srcUrl} height={20} />
                  </foreignObject>
                </g>
              ) : (
                <g style={{ pointerEvents: 'none' }}>
                  <text x={slotX + slotW / 2} y={audioSlotMidY - 4}
                    textAnchor="middle" fontSize="16" fill={C.textDim}
                    style={{ userSelect: 'none' }}>+</text>
                  <text x={slotX + slotW / 2} y={audioSlotMidY + 11}
                    textAnchor="middle" fontSize="9.5" fill={C.textDim}
                    style={{ userSelect: 'none' }}>Click to add audio</text>
                </g>
              )}
              {!audioEnabled && (
                <rect x={slotX} y={audioSlotY} width={slotW} height={AUDIO_SLOT_H} rx="5" fill="transparent"
                  onClick={(e) => handleFieldChange(e, sourceIndex, 'playSFX', true, 'sfxFilename')} />
              )}
              {audioEnabled && (
                <DeleteBtn cx={slotX + slotW - 10} cy={audioSlotY + 10} r={7}
                  onClick={(e) => {
                    e.stopPropagation();
                    onGoalFieldChange?.(sourceIndex, 'playSFX', false);
                  }} />
              )}
            </g>

            {/* ── Trash delete button ───────────────────────────────────────────────────────────────────────── */}
            <DeleteBtn
              cx={trashCX}
              cy={trashCY}
              r={9}
              onClick={(e) => { e.stopPropagation(); if (onRemoveGoal) onRemoveGoal(sourceIndex); }}
            />

          </g>
        );
      })}

    </g>
  );
}
